import React, { memo, useState } from "react";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import ReactCountDown from "react-countdown";
import { PlayerBuildingState } from "../../types/buildings";
import { useAppTranslation } from "../../i18n/useAppTranslation";

interface BuildingStatusProps {
  x: number;
  y: number;
  name: string;
  level: number;
  progress: number; // 0-100 percentage
  scale?: number;
  capacity?: number;
  filled?: number;
  isDungeon?: boolean;
  isCollectable?: boolean;
  health?: number;
  isBarracks?: boolean;
  buildingState?: PlayerBuildingState;
  upgradeTime?: string;
  trainingQueue?: number; // Number of troops in training queue
  maxTrainingQueue?: number; // Maximum capacity of training queue
  buildingId?: number; // Building ID for API calls
  buildingData?: any; // Building data for pricing information
  onInstantUpgrade?: () => void; // Callback for instant upgrade button
}

/**
 * BuildingStatus component - displays name, level, and progress bar
 * Similar to the image showing "IRON MINE" with progress bar and level "1"
 */
const BuildingStatus: React.FC<BuildingStatusProps> = ({
  x,
  y,
  name,
  level,
  progress,
  scale = 1,
  capacity = 0,
  filled = 0,
  isCollectable = false,
  health,
  isBarracks = false,
  isDungeon = false,
  buildingState,
  upgradeTime,
  trainingQueue = 0,
  maxTrainingQueue = 0,
  buildingId,
  buildingData,
  onInstantUpgrade,
}) => {
  const { t } = useAppTranslation(["village"]);
  const [isHovered, setIsHovered] = useState(false);
  // Calculate fill percentage for the progress bar
  const fillPercentage = health;

  // Use fill percentage if available, otherwise use progress
  // Prevent division by zero by checking if maxTrainingQueue > 0
  const displayProgress =
    isBarracks && maxTrainingQueue > 0
      ? Math.min(100, (filled / capacity) * 100)
      : fillPercentage || 0;

  // Create text styles
  const nameTextStyle = new PIXI.TextStyle({
    fontFamily: "changa",
    fontSize: isDungeon ? 60 : 40,
    fontWeight: "bold",
    fill: 0xf2f2f2,
    stroke: 0x000000,
    strokeThickness: 4,
    dropShadow: true,
    dropShadowColor: 0x000000,
    dropShadowDistance: isDungeon ? 4 : 2,
    dropShadowAngle: Math.PI / 6,
    dropShadowBlur: 4,
  });

  const levelTextStyle = new PIXI.TextStyle({
    fontFamily: "changa",
    fontSize: 40,
    fontWeight: "bold",
    fill: 0xffffff,
    stroke: 0x000000,
    strokeThickness: 4,
  });

  const countDownTextStyle = new PIXI.TextStyle({
    fontFamily: "changa",
    fontSize: 24,
    fontWeight: "500",
    stroke: 0x000000,
    strokeThickness: 4,
    fill: 0xffffff,
  });

  const buttonTextStyle = new PIXI.TextStyle({
    fontFamily: "changa",
    fontSize: 45,
    fontWeight: "bold",
    fill: 0xffffff,
    stroke: 0x000000,
    strokeThickness: 4,
  });

  // Calculate upgrade progress if building is upgrading
  const getUpgradeProgress = () => {
    if (buildingState !== PlayerBuildingState.Upgrading || !upgradeTime) {
      return 0;
    }

    // Calculate progress based on time remaining
    const now = new Date().getTime();
    const upgradeEndTime = new Date(upgradeTime).getTime();

    // If upgrade time is in the past, it's complete
    if (now >= upgradeEndTime) {
      return 100;
    }

    // Calculate based on a reasonable upgrade duration
    // You can adjust this based on your game's upgrade mechanics
    const estimatedUpgradeDuration = Math.max(
      1 * 60 * 60 * 1000, // Minimum 1 hour
      (upgradeEndTime - now) * 2 // Assume we're halfway through when we start calculating
    );

    const upgradeStartTime = upgradeEndTime - estimatedUpgradeDuration;
    const totalTime = upgradeEndTime - upgradeStartTime;
    const elapsedTime = now - upgradeStartTime;

    return Math.min(100, Math.max(0, (elapsedTime / totalTime) * 100));
  };

  // Draw progress bar
  const drawProgressBar = React.useCallback(
    (g) => {
      g.clear();

      // Background bar (solid dark background - no stripe pattern)
      g.beginFill(0x333333);
      g.drawRoundedRect(0, 0, 200, 20, 10);
      g.endFill();

      // Determine progress value and color based on building state
      let progressValue;
      let fillColor;

      if (buildingState === PlayerBuildingState.Upgrading) {
        // For upgrading buildings, show upgrade progress with yellow color
        progressValue = getUpgradeProgress();
        fillColor = 0xffd700; // Gold/Yellow color for upgrading
      } else if (isCollectable) {
        progressValue = displayProgress;
        fillColor = 0x4caf50; // Always green for collectable
      } else if (isBarracks) {
        progressValue = displayProgress;
        // For barracks, capacity fill - REVERSE logic: green when low, red when high/full
        if (displayProgress >= 80) {
          fillColor = 0xf44336; // Red when almost full (80% or more)
        } else if (displayProgress >= 60) {
          fillColor = 0xff9800; // Orange (60-79%)
        } else if (displayProgress >= 40) {
          fillColor = 0xffc107; // Amber/yellow (40-59%)
        } else if (displayProgress >= 20) {
          fillColor = 0x8bc34a; // Light green (20-39%)
        } else {
          fillColor = 0x4caf50; // Green when queue is low/empty (less than 20%)
        }
      } else {
        progressValue = displayProgress;
        // Health-based gradient for regular buildings: red (low) -> orange -> yellow -> green (high)
        if (displayProgress >= 80) {
          fillColor = 0x4caf50; // Green when health is high (80% or more)
        } else if (displayProgress >= 60) {
          fillColor = 0x8bc34a; // Light green (60-79%)
        } else if (displayProgress >= 40) {
          fillColor = 0xffc107; // Amber/yellow (40-59%)
        } else if (displayProgress >= 20) {
          fillColor = 0xff9800; // Orange (20-39%)
        } else {
          fillColor = 0xf44336; // Red when health is low (less than 20%)
        }
      }

      // Progress fill with dynamic color
      g.beginFill(fillColor);
      g.drawRoundedRect(0, 0, 200 * (progressValue / 100), 20, 10);
      g.endFill();

      // Border
      g.lineStyle(2, 0x222222);
      g.drawRoundedRect(0, 0, 200, 20, 10);
    },
    [displayProgress, isCollectable, isBarracks, buildingState, upgradeTime]
  );

  // Draw upgrade progress bar (yellow progress bar for upgrading buildings)
  const drawUpgradeProgressBar = React.useCallback(
    (g) => {
      g.clear();

      // Background bar
      g.beginFill(0x333333);
      g.drawRoundedRect(0, 0, 200, 20, 10);
      g.endFill();

      // Calculate upgrade progress based on time
      const upgradeProgress = getUpgradeProgress();

      // Yellow progress fill for upgrading
      g.beginFill(0xffd700); // Gold/Yellow color
      g.drawRoundedRect(0, 0, 200 * (upgradeProgress / 100), 20, 10);
      g.endFill();

      // Border
      g.lineStyle(2, 0x222222);
      g.drawRoundedRect(0, 0, 200, 20, 10);
    },
    [upgradeTime, buildingState]
  );

  // Draw instant upgrade button background
  const drawInstantUpgradeButton = React.useCallback((g) => {
    g.clear();
    // Button background with gradient
    g.beginFill(0x197b86, 0.5);
    g.drawRoundedRect(0, 0, 410, 85, 9);
    g.endFill();

    g.lineStyle(2, 0x2e7d32);
    g.drawRoundedRect(0, 0, 410, 85, 9);
  }, []);

  return (
    <>
      {buildingState != PlayerBuildingState.Upgrading && (
        <Container x={x} y={y} scale={scale}>
          {/* Name label */}
          <Text
            text={name.toUpperCase()}
            x={isDungeon ? -250 : 0}
            rotation={isDungeon ? 0.6 : 0}
            y={isDungeon ? 45 : -30}
            style={nameTextStyle}
          />

          {/* Progress bar */}
          {!isDungeon && (
            <>
              <Graphics draw={drawProgressBar} y={40} />
            </>
          )}
          {/* Level badge */}
          {!isDungeon && (
            <Container x={-78} y={-10}>
              <Sprite
                image="/assets/images/buttons/active-btn.png"
                x={0}
                y={4}
                width={60}
                height={60}
              />
              {/* <Graphics draw={drawLevelBadge} /> */}
              <Text
                text={level.toString()}
                x={30}
                y={30}
                anchor={0.5}
                style={levelTextStyle}
              />
            </Container>
          )}
        </Container>
      )}
      {buildingState === PlayerBuildingState.Upgrading && upgradeTime && (
        <Container x={x} y={y - 150} scale={scale}>
          <Text
            text={
              level === 0
                ? t("village:status.building")
                : t("village:status.upgrading")
            }
            style={levelTextStyle}
          />

          {/* Yellow progress bar for upgrading */}
          <Graphics draw={drawUpgradeProgressBar} y={65} />

          <ReactCountDown
            date={new Date(upgradeTime)}
            renderer={({ days, hours, minutes, seconds }) => {
              return (
                <Text
                  x={100}
                  y={110}
                  anchor={0.5}
                  text={`${days}${t("village:time.short.days")}:${hours}${t(
                    "village:time.short.hours"
                  )}:${minutes}${t("village:time.short.minutes")}`}
                  style={countDownTextStyle}
                />
              );
            }}
          />

          {/* Instant Upgrade Button */}
          {buildingData && buildingId && (
            <Container
              y={150}
              x={15}
              anchor={0.5}
              eventMode="static"
              cursor="pointer"
              scale={.85}
              onpointerup={onInstantUpgrade}
            >
              <Sprite
                x={100}
                y={48}
                scale={isHovered ? 1.05 : 1}
                anchor={0.5}
                width={410}
                pointerover={() => setIsHovered(true)}
                pointerout={() => setIsHovered(false)}
                height={110}
                image="/assets/images/buttons/blue-btn-wide.png" // your custom button background
              />
              {/* <Graphics x={-100} anchor={0.5} draw={drawInstantUpgradeButton} /> */}
              <Text
                text={t("village:actions.instantUpgrade")}
                x={105}
                y={38}
                anchor={0.5}
                style={buttonTextStyle}
              />
            </Container>
          )}
        </Container>
      )}
    </>
  );
};

export default memo(BuildingStatus, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.x === nextProps.x &&
    prevProps.y === nextProps.y &&
    prevProps.scale === nextProps.scale &&
    prevProps.name === nextProps.name &&
    prevProps.level === nextProps.level &&
    prevProps.progress === nextProps.progress &&
    prevProps.capacity === nextProps.capacity &&
    prevProps.filled === nextProps.filled &&
    prevProps.isCollectable === nextProps.isCollectable &&
    prevProps.health === nextProps.health &&
    prevProps.trainingQueue === nextProps.trainingQueue &&
    prevProps.maxTrainingQueue === nextProps.maxTrainingQueue &&
    prevProps.isBarracks === nextProps.isBarracks &&
    prevProps.buildingState === nextProps.buildingState &&
    prevProps.upgradeTime === nextProps.upgradeTime &&
    prevProps.buildingId === nextProps.buildingId &&
    JSON.stringify(prevProps.buildingData) ===
      JSON.stringify(nextProps.buildingData)
  );
});
